import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Calendar, Info, ArrowLeft, Check } from 'lucide-react';
import Layout from '../Layout';
import { LoisirManager } from '../../helpers/loisir';
import { showToast } from '../../helpers/customToast';
import { checkLoggedIn, getCurrentUserData, getCurrentUserAddress, createGuestAccount, saveAddress, updateUser } from '../../helpers/user';
import { generatePassword, countriesList } from '../../helpers/tools';
import { createPaymentLink, createSubscriptionLink } from '../../helpers/stripe';
import DeliverySelection from '../DeliverySelection';
import { getData } from '../../helpers/ApiSettings';

const LoisirCheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const [loisirSelection, setLoisirSelection] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedRelayPoint, setSelectedRelayPoint] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    postal_code: '',
    comments: '',
    country: ''
  });

  useEffect(() => {
    const initializeCheckout = async () => {
      setLoading(true);
      try {
        // Check if user is logged in
        await checkAuthentication();

        // Get loisir selection from session storage
        const selection = LoisirManager.getSelection();
        if (!selection || !selection.formula) {
          showToast('Veuillez d\'abord sélectionner une formule', 'error');
          window.location.href = '/loisirs/selection';
          return;
        }

        setLoisirSelection(selection);
        const { duration } = selection;
        setSelectedDuration(duration);

        // Check final availability of selected products
        if (selection.products && selection.startDate && selection.endDate) {
          await checkFinalAvailability(selection);
        }
      } catch (error) {
        console.error('Error initializing checkout:', error);
        showToast('Une erreur est survenue lors de l\'initialisation du paiement', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializeCheckout();
  }, []);

  const checkFinalAvailability = async (selection) => {
    try {
      const productSelections = selection.products.map(item => ({
        productId: item.productId,
        quantity: item.quantity
      }));

      const availabilityResults = await LoisirManager.checkProductsAvailability(
        productSelections,
        selection.startDate,
        selection.endDate
      );

      const unavailableProducts = availabilityResults.filter(result => !result.isAvailable);
      
      if (unavailableProducts.length > 0) {
        showToast('Certains articles ne sont plus disponibles dans les quantités demandées. Veuillez modifier votre sélection.', 'error');
        window.location.href = '/loisirs/selection';
        return;
      }

      setAvailabilityChecked(true);
    } catch (error) {
      console.error('Error checking final availability:', error);
      showToast('Erreur lors de la vérification de disponibilité', 'error');
    }
  };

  const checkAuthentication = async () => {
    try {
      const authStatus = await checkLoggedIn();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userData = await getCurrentUserData();
        let userAddress;
        if (userData.user_address?.id) {
          userAddress = await getCurrentUserAddress(userData.user_address.documentId);
        }
        setCurrentUserData(userData);

        setFormData(prevFormData => ({
          ...prevFormData,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          address_1: userAddress?.address_1 || '',
          address_2: userAddress?.address_2 || '',
          city: userAddress?.city || '',
          postal_code: userAddress?.postal_code || '',
          country: userAddress?.country || '',
          phone: userAddress?.phone || ''
        }));
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      showToast('Échec de la vérification du statut d\'authentification', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySearch = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, country: value }));

    if (value.length > 1) {
      const results = await countriesList(value);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const selectCountry = (country) => {
    setFormData(prev => ({ ...prev, country: country }));
    setSearchResults([]);
  };

  const validateCoupon = async (code) => {
    try {
      setIsValidatingCoupon(true);
      const response = await getData(`/coupons?filters[code][$eq]=${code}&populate=*`);

      if (!response.data.data?.[0]) {
        showToast('Code promo invalide', 'error');
        return;
      }

      const coupon = response.data.data[0];

      // Check if coupon is active
      if (!coupon.is_active) {
        showToast('Ce code promo n\'est plus valide', 'error');
        return;
      }

      // Check if coupon is applicable to formulas
      if (coupon.applicable_to_formulas === false) {
        showToast('Ce code promo n\'est pas valide pour les formules loisirs', 'error');
        return;
      }

      // Check validity period
      const now = new Date();
      const validFrom = new Date(coupon.valid_from);
      const validUntil = new Date(coupon.valid_until);

      if (now < validFrom || now > validUntil) {
        showToast('Ce code promo a expiré', 'error');
        return;
      }

      // Calculate total price
      const priceDetails = LoisirManager.calculatePrice(
        loisirSelection.formula,
        loisirSelection.duration
      );
      const totalPrice = selectedDuration === 30
        ? priceDetails.price
        : priceDetails.price + deliveryPrice;

      // Check minimum amount
      if (coupon.min_amount && totalPrice < coupon.min_amount) {
        showToast(`Le montant minimum d'achat pour ce code est de ${coupon.min_amount}€`, 'error');
        return;
      }

      // Check if authorised for only one user
      if (coupon.user) {
        if (isAuthenticated) {
          if (currentUserData.id !== coupon.user.id) {
            showToast(`Vous ne pouvez pas utiliser ce coupon.`, 'error');
            return;
          }
        } else {
          showToast(`Merci de vous connecter afin de vérifier ce coupon.`, 'error');
          return;
        }
      }

      setAppliedCoupon(coupon);
      showToast('Code promo appliqué avec succès', 'success');
    } catch (error) {
      console.error('Error validating coupon:', error);
      showToast('Erreur lors de la validation du code promo', 'error');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;

    const priceDetails = LoisirManager.calculatePrice(
      loisirSelection.formula,
      loisirSelection.duration
    );
    const totalPrice = selectedDuration === 30
      ? priceDetails.price
      : priceDetails.price + deliveryPrice;

    let discount = 0;
    if (appliedCoupon.is_percentage) {
      discount = (totalPrice * appliedCoupon.amount) / 100;
    } else {
      discount = appliedCoupon.amount;
    }

    return Math.min(discount, totalPrice);
  };

  const handleDeliveryMethodChange = (method) => {
    setSelectedDeliveryMethod(method);
    // For 30-day subscriptions, delivery is always free
    if (selectedDuration === 30) {
      setDeliveryPrice(0);
    } else {
      setDeliveryPrice(0);
    }
  };

  const handleZoneChange = (zone) => {
    if (zone) {
      setSelectedZone(zone.id);
      // For 30-day subscriptions, delivery is always free
      if (selectedDuration === 30) {
        setDeliveryPrice(0);
      } else {
        setDeliveryPrice(zone.price || 0);
      }
    } else {
      setSelectedZone('');
    }
  };

  const handleRelayPointChange = (point) => {
    setSelectedRelayPoint(point);
    if (point) {
      // For 30-day subscriptions, delivery is always free
      if (selectedDuration === 30) {
        setDeliveryPrice(0);
      } else {
        setDeliveryPrice(point.price);
      }
    }
  };

  const validateForm = () => {
    const requiredFields = {
      first_name: 'Prénom',
      last_name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address_1: 'Adresse',
      country: 'Pays',
      city: 'Ville',
      postal_code: 'Code Postal'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]?.trim()) {
        throw new Error(`Le champ "${label}" est requis.`);
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Veuillez saisir une adresse e-mail valide');
    }

    if (!selectedDeliveryMethod) {
      throw new Error('Veuillez sélectionner un mode de livraison');
    }

    if (selectedDeliveryMethod === 'home_delivery' && !selectedZone) {
      throw new Error('Veuillez sélectionner une zone de livraison');
    }

    if (selectedDeliveryMethod === 'relay_point' && !selectedRelayPoint) {
      throw new Error('Veuillez sélectionner un point relais');
    }

    if (!availabilityChecked) {
      throw new Error('Vérification de disponibilité en cours...');
    }
  };

  const handleGuestCheckout = async () => {
    try {
      const password = generatePassword();
      const userData = {
        username: formData.email,
        password,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name
      };

      const newUser = await createGuestAccount(userData);
      if (!newUser.success) {
        throw new Error('Failed to create guest account');
      }
      setCurrentUserData(newUser.user);

      return newUser.user.id;
    } catch (error) {
      console.error('Guest checkout failed:', error);
      throw new Error('Failed to create account for checkout');
    }
  };

  const handleAddress = async (userId) => {
    const addressData = {
      address_1: formData.address_1,
      address_2: formData.address_2,
      country: formData.country,
      postal_code: formData.postal_code,
      city: formData.city,
      phone: formData.phone,
      user: userId
    };

    try {
      await saveAddress({ data: addressData });
    } catch (error) {
      console.error('Failed to save address:', error);
      throw new Error('Failed to save shipping address');
    }
  };

  const processLoisirOrder = async (orderData) => {
    try {
      // Create the loisir order
      const orderResult = await LoisirManager.createLoisirOrder({
        ...orderData,
        status: 'pending_payment',
        is_subscription: orderData.durationDays === 30,
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      if (orderData.durationDays === 30) {
        const subscriptionLinkResult = await createSubscriptionLink({
          orderId: orderResult.orderId,
          documentId: orderResult.documentId,
          monthlyAmount: orderData.totalPrice || 0,
          customerEmail: formData.email,
          firstName: formData.first_name,
          lastName: formData.last_name,
        });
  
        // Clear the loisir selection after successful order creation
        LoisirManager.clearSelection();
  
        return {
          orderId: orderResult.orderId,
          paymentUrl: subscriptionLinkResult.url
        };
      }

      // Create Stripe payment link
      const paymentLinkResult = await createPaymentLink({
        orderId: orderResult.orderId,
        documentId: orderResult.documentId,
        totalAmount: orderData.totalPrice,
        customerEmail: formData.email,
        firstName: formData.first_name,
        lastName: formData.last_name,
        orderType: 'formula'
      });

      // Clear the loisir selection after successful order creation
      LoisirManager.clearSelection();

      return {
        orderId: orderResult.orderId,
        paymentUrl: paymentLinkResult.url
      };
    } catch (error) {
      console.error('Error in processLoisirOrder:', error);
      throw new Error(error.message || 'Failed to process order');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      validateForm();
      let userId;

      if (!isAuthenticated) {
        userId = await handleGuestCheckout();
      } else {
        userId = currentUserData.id;
        await updateUser(userId, {
          first_name: formData.first_name,
          last_name: formData.last_name
        });
      }
      await handleAddress(userId);

      // Calculate total price
      const priceDetails = LoisirManager.calculatePrice(
        loisirSelection.formula,
        loisirSelection.duration
      );

      const totalPrice = ((selectedDuration === 30
        ? priceDetails.price
        : priceDetails.price + deliveryPrice)) - calculateDiscount();

      const orderData = {
        userId,
        formulaId: loisirSelection.formula.id,
        durationDays: loisirSelection.duration,
        selectedProducts: loisirSelection.products.map(p => ({
          productId: p.productId,
          quantity: p.quantity
        })),
        startDate: loisirSelection.startDate,
        endDate: loisirSelection.endDate,
        totalPrice,
        depositAmount: priceDetails.deposit,
        deliveryMethod: selectedDeliveryMethod,
        deliveryPrice: selectedDuration === 30 ? 0 : deliveryPrice,
        comments: formData.comments,
        coupon: appliedCoupon ? {
          id: appliedCoupon.id,
          code: appliedCoupon.code,
          discount: calculateDiscount()
        } : null,
        userDetails: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          address_1: formData.address_1,
          address_2: formData.address_2,
          country: formData.country,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone,
        },
        relayPoint: selectedDeliveryMethod === 'relay_point' ? selectedRelayPoint.id : null
      };

      const result = await processLoisirOrder(orderData);

      // Redirect to Stripe payment page
      window.location.href = result.paymentUrl;
    } catch (error) {
      console.error('Checkout failed:', error);
      showToast(error.message || 'Une erreur est survenue, merci de réessayer.', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const getTotalSelectedItems = () => {
    if (!loisirSelection?.products) return 0;
    return loisirSelection.products.reduce((sum, item) => sum + (item.quantity || 1), 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Initialisation du paiement...</p>
        </div>
      </Layout>
    );
  }

  if (!loisirSelection) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-warning">
            Aucune formule sélectionnée. Veuillez d'abord <Link to="/loisirs/selection">choisir une formule</Link>.
          </div>
        </div>
      </Layout>
    );
  }

  const { formula, duration, products, startDate, endDate } = loisirSelection;
  const priceDetails = LoisirManager.calculatePrice(formula, duration);

  return (
    <Layout>
      <div className="container my-5">
        <div className="d-flex align-items-center mb-4">
          <Link to="/loisirs/selection" className="btn btn-outline-secondary me-3">
            <ArrowLeft size={16} className="me-2" />
            Retour
          </Link>
          <h1 className="mb-0">Paiement de votre formule Loisirs</h1>
        </div>

        {!availabilityChecked && (
          <div className="alert alert-info mb-4">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              Vérification finale de la disponibilité de vos articles...
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">Déjà client ?</h5>
                  <p className="text-muted mb-0">
                    Connectez-vous pour un paiement plus rapide et suivre vos commandes
                  </p>
                </div>
                <div className="col-md-4 text-end">
                  <Link
                    to="/connexion?redirect=loisirs/checkout"
                    className="btn btn-outline-primary"
                  >
                    Se connecter
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Dépôt de Garantie</h3>
                <div className="alert alert-info" role="alert">
                  <Info size={18} className="me-2" />
                  Un dépôt de garantie de {priceDetails.deposit.toFixed(2)}€ sera requis pour votre réservation.
                  Vous recevrez un email de Swikly pour effectuer ce dépôt de manière sécurisée.
                  Le dépôt ne sera pas débité de votre compte, mais simplement pré-autorisé.
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card mb-4 overflow-visible">
                <div className="card-body">
                  <h3 className="card-title">Informations Personnelles</h3>
                  {isAuthenticated && (
                    <p className='mb-3 text-secondary'><i>Votre compte sera mis à jour avec ces informations.</i></p>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="last_name" className="form-label">Nom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="first_name" className="form-label">Prénom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label">E-mail <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        readOnly={isAuthenticated}
                        disabled={isAuthenticated}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label">Téléphone <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="address" className="form-label">Adresse <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="address_1"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        required
                      />
                      <input
                        type="text"
                        className="form-control"
                        id="address_2"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="country" className="form-label">Pays <span className="text-danger">*</span></label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleCountrySearch}
                          autoComplete="off"
                          required
                        />
                        {searchResults.length > 0 && (
                          <div className="position-absolute w-100 mt-1 bg-light border rounded shadow-sm" style={{ zIndex: 1000, height: 100, overflow: 'scroll' }}>
                            {searchResults.map((country, index) => (
                              <div
                                key={index}
                                className="p-2 cursor-pointer hover:bg-white"
                                style={{ cursor: 'pointer' }}
                                onClick={() => selectCountry(country.name_fr)}
                              >
                                {country.name_fr}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="city" className="form-label">Ville <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="postal_code" className="form-label">Code Postal <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Informations Supplémentaires <small className='text-secondary'><i>(optionel)</i></small></h3>
                  <p>Si vous avez des demandes particulières merci de les inqiquer ici.</p>
                  <div className="col-md-12">
                    <textarea
                      className="form-control"
                      id="comments"
                      name="comments"
                      rows="3"
                      onChange={handleInputChange}
                      value={formData.comments}
                    ></textarea>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Récapitulatif</h3>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="my-0">Formule {formula.name}</h6>
                    <small className="text-muted">
                      <Calendar size={14} className="me-1" />
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </small>
                    <br />
                    <small className="text-muted">
                      Durée : {duration} jours
                    </small>
                  </div>
                  <span className="text-muted">{priceDetails.price.toFixed(2)}€</span>
                </div>

                <hr className="my-3" />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Articles sélectionnés</h6>
                  <span className="badge bg-primary">{getTotalSelectedItems()}</span>
                </div>
                <ul className="list-group mb-3">
                  {products.map(item => (
                    <li key={item.productId} className="list-group-item d-flex justify-content-between lh-sm">
                      <div>
                        <h6 className="my-0">{item.product.title}</h6>
                        {item.quantity > 1 && (
                          <small className="text-muted">Quantité: {item.quantity}</small>
                        )}
                      </div>
                      {item.quantity > 1 && (
                        <span className="badge bg-secondary">{item.quantity}</span>
                      )}
                    </li>
                  ))}
                </ul>

                <hr className="my-4" />
                <div className="mb-3">
                  <label htmlFor="coupon" className="form-label">Code promo</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Entrez votre code"
                      disabled={isValidatingCoupon || appliedCoupon}
                    />
                    {!appliedCoupon ? (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => validateCoupon(couponCode)}
                        disabled={!couponCode || isValidatingCoupon}
                      >
                        {isValidatingCoupon ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : 'Appliquer'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponCode('');
                        }}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <small className="text-success">
                      Code promo appliqué : {appliedCoupon.is_percentage ? `${appliedCoupon.amount}%` : `${appliedCoupon.amount}€`} de réduction
                    </small>
                  )}
                </div>

                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Sous-total</span>
                  <span>{priceDetails.price.toFixed(2)}€</span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>
                    Frais de livraison
                    {selectedDuration === 30 && (
                      <span className="badge bg-success ms-2">GRATUIT</span>
                    )}
                  </span>
                  <span>
                    {selectedDuration === 30 ? (
                      <span className="text-decoration-line-through text-muted me-2">
                        {deliveryPrice > 0 ? `${deliveryPrice.toFixed(2)}€` : '0€'}
                      </span>
                    ) : null}
                    {selectedDuration === 30 ? '0€' : `${deliveryPrice.toFixed(2)}€`}
                  </span>
                </div>

                {appliedCoupon && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Réduction</span>
                    <span className="text-success">-{calculateDiscount().toFixed(2)}€</span>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="m-0">Total</h5>
                  <h5 className="m-0">
                    {((selectedDuration === 30
                      ? priceDetails.price
                      : (priceDetails.price + deliveryPrice)) - calculateDiscount()
                    ).toFixed(2)}€
                  </h5>
                </div>

                {selectedDuration === 30 && (
                  <div className="mt-2">
                    <small className="text-success">
                      <i className="fas fa-gift me-1"></i>
                      Livraison gratuite incluse dans votre abonnement !
                    </small>
                  </div>
                )}

                <div className="mt-3">
                  <h6>Dépôt de garantie: {priceDetails.deposit.toFixed(2)}€</h6>
                  <p className="text-muted small">Ce montant ne sera pas débité de votre compte.</p>
                </div>
              </div>
            </div>

            <DeliverySelection
              selectedMethod={selectedDeliveryMethod}
              selectedZone={selectedZone}
              selectedRelayPoint={selectedRelayPoint}
              deliveryPrice={deliveryPrice}
              onMethodChange={handleDeliveryMethodChange}
              onZoneChange={handleZoneChange}
              onRelayPointChange={handleRelayPointChange}
              isSubscription={selectedDuration === 30}
              freeDelivery={selectedDuration === 30}
            />

            {selectedDuration === 30 && (
              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Informations d'abonnement</h3>
                  <div className="alert alert-success" role="alert">
                    <div className="d-flex">
                      <div className="me-3">
                        <Check size={20} className="text-success" />
                      </div>
                      <div>
                        <p className="mb-1"><strong>Vous avez choisi l'abonnement mensuel</strong></p>
                        <p className="mb-0">Votre abonnement sera automatiquement renouvelé tous les mois, jusqu'à ce que vous décidiez de l'arrêter.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="subscription-agreement"
                        name="subscription-agreement"
                        required
                      />
                      <label className="form-check-label" htmlFor="subscription-agreement">
                        J'accepte que mon compte soit débité automatiquement chaque mois pour un montant de {priceDetails.price.toFixed(2)}€ jusqu'à ce que j'annule mon abonnement.
                        <br />
                        <small className="text-success">
                          La livraison est gratuite pour les abonnements mensuels.
                        </small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Mode de paiement</h3>
                {selectedDuration === 30 ? (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <input type="radio" name="payment_method" defaultChecked />
                      <CreditCard size={18} className="ms-2 me-2" />
                      <div>
                        <span>Carte bancaire (abonnement Stripe)</span>
                        <p className="text-muted small mb-0">Votre carte sera débitée tous les mois automatiquement</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <input type="radio" defaultChecked /> Carte bancaire (Stripe) <CreditCard size={18} />
                  </>
                )}
              </div>
            </div>

            <div className="form-check mb-5">
              <input
                type="checkbox"
                name="acceptance"
                id="acceptance"
                required
                className="form-check-input"
              />
              <label htmlFor="acceptance" className="form-check-label">
                J'accepte les <a href="/page/cgv" target="_blank">Conditions Générales</a> <span className="text-danger">*</span>
              </label>
            </div>

            <button 
              type="submit" 
              onClick={handleSubmit} 
              className="btn btn-primary btn-lg w-100"
              disabled={!availabilityChecked}
            >
              {availabilityChecked ? 'Commander & Payer' : 'Vérification en cours...'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoisirCheckoutPage;